import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * AdjectiveFlip — "A House Big?!" English→Arabic word-order flipper.
 *
 * Each row shows an English phrase (describer first, highlighted) and a
 * pulsing flip button; tapping it reveals the Arabic phrase in noun-first
 * order — the describer highlighted and each word glossed underneath, so the
 * reversed order is visible at a glance — and plays it. Completes when every
 * phrase has been flipped.
 */
function EnglishPhrase({ phraseEn, adjEn }) {
  const at = phraseEn.indexOf(adjEn);
  if (at < 0) return phraseEn;
  return (
    <>
      {phraseEn.slice(0, at)}
      <span style={{ color: "var(--tag-violet)" }}>{adjEn}</span>
      {phraseEn.slice(at + adjEn.length)}
    </>
  );
}

function ArabicPhrase({ pair, audio }) {
  return (
    <>
      <div dir="rtl" lang="ar" className="flex items-start justify-center gap-3">
        <span className="text-center">
          <span className="font-arabic text-3xl leading-[1.8] block" style={{ color: "var(--parchment)" }}>
            {pair.noun_ar}
          </span>
          <span className="text-[11px] block" style={{ color: "var(--parchment-dim)" }}>{pair.noun_en}</span>
        </span>
        <span className="text-center">
          <span className="font-arabic text-3xl leading-[1.8] block" style={{ color: "var(--tag-violet)" }}>
            {pair.adj_ar}
          </span>
          <span className="text-[11px] block" style={{ color: "var(--tag-violet)" }}>{pair.adj_en}</span>
        </span>
      </div>
      {audio.audioAllowed && (
        <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
          <PlayButton
            text={`${pair.noun_ar} ${pair.adj_ar}`}
            playingText={audio.playingText}
            onPlay={audio.play}
          />
        </p>
      )}
    </>
  );
}

export default function AdjectiveFlip({ data, onComplete, audio }) {
  const [flippedIds, setFlippedIds] = useState(() => new Set());

  const pairs = data.pairs;
  const unitLabel = data.unit_label || "phrases";
  const allFlipped = flippedIds.size >= pairs.length;

  useEffect(() => {
    if (allFlipped) onComplete();
  }, [allFlipped, onComplete]);

  const handleFlip = (pair) => {
    if (flippedIds.has(pair.id)) return;
    setFlippedIds((prev) => new Set(prev).add(pair.id));
    audio.play(`${pair.noun_ar} ${pair.adj_ar}`);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{flippedIds.size}/{pairs.length} {unitLabel} flipped</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {pairs.map((pair) => {
          const flipped = flippedIds.has(pair.id);
          return (
            <div
              key={pair.id}
              className="rounded-xl px-4 py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="text-center">
                <p className="text-xl m-0" style={{ color: "var(--parchment)" }}>
                  <EnglishPhrase phraseEn={pair.phrase_en} adjEn={pair.adj_en} />
                </p>
                <p className="text-[11px] mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
                  describer first
                </p>
              </div>

              <div className="flex justify-center">
                {flipped ? (
                  <span className="text-xl" aria-hidden="true" style={{ color: "var(--gold-dim)" }}>→</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleFlip(pair)}
                    className="rounded-lg px-3.5 py-2 text-2xl leading-none transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                    }}
                    title="Tap to flip into Arabic order"
                  >
                    {data.button_label || "⇄"}
                  </button>
                )}
              </div>

              <div className="text-center">
                {flipped ? (
                  <ArabicPhrase pair={pair} audio={audio} />
                ) : (
                  <p className="font-arabic text-3xl m-0" aria-hidden="true" style={{ color: "var(--parchment-dim)", opacity: 0.35 }}>
                    ؟
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allFlipped && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
